// Display scale: how big the notation and the chord charts are drawn.
//
// This replaces three S/M/L tier settings that were read through TWO unrelated
// mechanisms and four value tables — the workbench scaled by setting px per
// staff space (`--score-space-size`) and deriving the system width from the
// container, while Score Studio scaled by fixing a system width in staff
// spaces and letting the SVG shrink to fit. Same button, same store field,
// different meaning per page: on a 900px canvas "S" was 8.5px/space on the
// workbench and 6.4px/space in the studio, and on a phone the two ladders
// barely overlapped. Three guessed tiers also meant "S is still too big" was
// a code change rather than a setting.
//
// Now there is ONE mechanism (px per staff space, system width derived from
// the container) and ONE unit: a percentage of a device-appropriate base.
// 100% is exactly the old 'standard'/'medium' tier, so nothing moves for
// anyone who never touched the control.
//
// Two scales remain, deliberately: chord charts and engraved notation are
// different content read at different densities, and a workspace cell can
// show both at once.

/** Percent bounds. 50% is genuinely small — smaller than any old tier. */
export const SCALE_MIN = 50;
export const SCALE_MAX = 200;
export const SCALE_DEFAULT = 100;
export const SCALE_STEP = 5;

/** Quick presets beside the slider, as percentages. */
export const SCALE_PRESETS = [60, 80, 100, 130, 170] as const;

/** A percentage, clamped and rounded to the slider's step. */
export function clampScale(value: unknown): number {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : SCALE_DEFAULT;
  const stepped = Math.round(n / SCALE_STEP) * SCALE_STEP;
  return Math.min(SCALE_MAX, Math.max(SCALE_MIN, stepped));
}

/** The unitless multiplier a CSS custom property carries. */
export function scaleFactor(percent: number): number {
  return clampScale(percent) / 100;
}

/**
 * A persisted `chartSize` tier as its equivalent percentage.
 *
 * The old px values were 18/22/28 against a 22px base, so the tiers land on
 * 82/100/127 — a user's chart keeps the size they chose.
 */
export function chartScaleFromTier(tier: unknown): number {
  if (tier === 'small') return 80;
  if (tier === 'large') return 125;
  return SCALE_DEFAULT;
}

/**
 * A persisted `scoreSize` tier as its equivalent percentage.
 *
 * Against the workbench's 10.5px base the old tiers were 8.5/10.5/12 →
 * 81/100/114. Score Studio's tiers were a different mechanism entirely and
 * cannot be carried over exactly; they map to the same percentages, which is
 * the closest honest answer (and its old 'M' was already ~10.6px/space).
 */
export function scoreScaleFromTier(tier: unknown): number {
  if (tier === 'compact') return 80;
  if (tier === 'large') return 115;
  return SCALE_DEFAULT;
}

/**
 * Which scale a workspace view draws at: the engraving's on the score surface
 * and under a voicing lens (whose overlays sit on engraved systems or a
 * diagram sheet sized with them), the chord size on the bare chart. The ⚙ view
 * settings and focus mode's slider both ask, so they cannot disagree.
 */
export function workspaceScaleKind(
  surface: 'chart' | 'score' | 'map',
  lens: 'none' | 'comping' | 'melody',
): 'score' | 'chart' {
  return surface === 'score' || lens !== 'none' ? 'score' : 'chart';
}

/** Slider label, e.g. "100%". */
export function scaleLabel(percent: number): string {
  return `${clampScale(percent)}%`;
}

/**
 * The custom properties a surface sets to carry its scale.
 *
 * The percentage never reaches a component's layout maths directly: it lands
 * on a CSS variable, the stylesheet multiplies the device-appropriate base by
 * it, and `useNotationMetrics` reads the resulting px back off the element.
 * One direction of travel, so the phone media query keeps working and nothing
 * has to duplicate the base sizes in JS.
 */
export function scaleVars(vars: {
  chart?: number;
  score?: number;
  chordName?: number;
}): Record<string, string> {
  const out: Record<string, string> = {};
  if (vars.chart !== undefined) out['--chart-scale'] = String(scaleFactor(vars.chart));
  if (vars.score !== undefined) out['--score-scale'] = String(scaleFactor(vars.score));
  if (vars.chordName !== undefined)
    out['--arrange-chord-name-scale'] = String(scaleFactor(vars.chordName));
  return out;
}

/** Fret labels share one height. Compress only their width at the gutter's
 * limit, so two-digit frets never get a smaller font or clip the SVG. */
export function fretNumberMetrics(percent: number, twoDigits: boolean) {
  const fontSize = 36 * scaleFactor(percent);
  return { fontSize, textLength: Math.min(24, fontSize * (twoDigits ? 1.1 : 0.55)) };
}

/** What occupies each sounding position on an Arrange fretboard. */
export type DiagramMarkers = 'dots' | 'notes' | 'intervals';
export function normalizeDiagramMarkers(value: unknown): DiagramMarkers {
  return value === 'notes' || value === 'intervals' ? value : 'dots';
}
